import { IProductPick, ItemAtPoint } from "contexts/RequestQuoteContext";
import { isSameDay } from "date-fns";
import { ERequestStatus, ERequestType, Items, Stop, TemplateStop } from "generated/graphql";
import moment from "moment";

export const isNormalInteger = (numberAsString: string) => {
  const n = parseInt(numberAsString, 10);
  return !isNaN(n) && n >= 0;
};

export const isFieldWithValue = (field: string) => field && field.replace(/\s/g, "").length;

export const formatDate = (daysToAdd: number = 0, dateString: string = undefined) => {
  let date = moment(dateString);
  date = date.isValid() ? date : moment();
  date = daysToAdd ? date.add(daysToAdd, "days") : date;
  return date.format("YYYY-MM-DD");
};

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const formatNumberOfDate = (text: any, dateDayeValue: number) => {
  switch (dateDayeValue) {
    case 1:
    case 21:
      return dateDayeValue + text.requestQuote.reccurence_day_number_completion_st;
    case 2:
    case 22:
      return dateDayeValue + text.requestQuote.reccurence_day_number_completion_nd;
    case 3:
    case 23:
      return dateDayeValue + text.requestQuote.reccurence_day_number_completion_rd;
    default:
      return dateDayeValue + text.requestQuote.reccurence_day_number_completion_th;
  }
};

const getDayLanguage = (day: string, text: any) => {
  switch (day) {
    case "Monday":
      return text.all.monday;
    case "Tuesday":
      return text.all.tuesday;
    case "Wednesday":
      return text.all.wednesday;
    case "Thursday":
      return text.all.thursday;
    case "Friday":
      return text.all.friday;
    case "Saturday":
      return text.all.saturday;

    default:
      return "";
  }
};

/**
 * this function get a string as date and returns day and number of date.
 * Notice: When the date isn't valid, it will choose by default the date of today.
 * If the day is Sunday, it will switch to monday
 * @param dateString
 * @returns array contains [number of date (1st, 2nd ...., 31st), and day of the date (Monday .... Saturday)]
 */
export const getRecurrenceMonthOptions = (dateString: string) => {
  let date = moment(dateString);

  date = !date.isValid() ? moment().add(1, "days") : date;
  date = days[date.day()] === "Sunday" ? date.add(1, "days") : date;

  const weekOfMonth = Math.ceil(date.date() / 7);

  const options = [
    {
      value: 0,
      description: (text: any) =>
        `${text.requestQuote.reccurence_every} ${formatNumberOfDate(text, date.date())} ${
          text.requestQuote.reccurence_month
        }`,
    },
    {
      value: weekOfMonth,
      description: (text: any) =>
        `${text.requestQuote[`reccurence_every_${weekOfMonth}_week`]} ${getDayLanguage(days[date.day()], text)}`,
    },
  ];

  if (weekOfMonth === 4) {
    options.push({
      value: 5,
      description: (text: any) =>
        `${text.requestQuote.reccurence_every_5_week} ${getDayLanguage(days[date.day()], text)}`,
    });
  }

  return options;
};

export const isSunday = (date: string) => {
  const d = moment(date);
  return d.day() === 0 || !d.isValid();
};
export const isDateIntervalCorrect = (startDate: string, endDate: string) => {
  const start = moment(startDate);
  const end = moment(endDate);
  return (
    isFieldWithValue(startDate) &&
    isFieldWithValue(endDate) &&
    start.isValid() &&
    end.isValid() &&
    (end.isAfter(start) || isSameDay(new Date(startDate), new Date(endDate))) &&
    !isSunday(startDate)
  );
};

export const isInCorrectIntervallTime = (slotTime: number) => slotTime >= 7 && slotTime <= 20;

export const isSlotIntervallCorrect = (slotStart: string, slotEnd: string) => {
  const start = parseInt(slotStart);
  const end = parseInt(slotEnd);

  return !isNaN(start) && !isNaN(end) && start < end;
};

export const renderRequestType = (requestType: ERequestType, text: any) => {
  switch (requestType) {
    case ERequestType.Mad:
      return text?.requestQuote.samapaser;
    case ERequestType.Nt:
      return text?.requestQuote.national_transport;
    case ERequestType.Tr:
      return text?.requestQuote.tour;
    default:
      return "";
  }
};

export enum ReccurenceType {
  NotRepeat = "NotRepeat",
  EveryDay = "EveryDay",
  Custom = "Custom",
  Days = "Days",
  Weeks = "Weeks",
  Months = "Months",
  Years = "Years",
}

export enum ReccurenceDay {
  Monday = "Monday",
  Tuesday = "Tuesday",
  Wednesday = "Wednesday",
  Thursday = "Thursday",
  Friday = "Friday",
  Saturday = "Saturday",
}

export enum ProductType {
  Couriers = "Couriers",
  Fragile = "Fragile",
  Food = "Food",
  Fresh_Products = "Fresh_Products",
  Other = "Other",
}

export enum RequestStatus {
  Requested = "Requested",
  Accepted = "Accepted",
  Approved = "Approved",
  Declined = "Declined",
}

export enum RequestType {
  Sama_Papser = "Sama_Papser",
  Tour = "Tour",
  National_Transport = "National_Transport",
}

/**
 * Cette fonction permet de récuperer les produits présents dans l'ancienne valeur du point et absent sur le nouveau point.
 * */

export const comparer = (oldItems: ItemAtPoint[]) =>
  function (item: ItemAtPoint) {
    return (
      oldItems?.filter(
        (old) => old?.product?._id === item?.product?._id && old?.product?.pickup === item?.product?.pickup
      ).length === 0
    );
  };

export const formatProduct = (product: IProductPick) => ({
  _id: product?._id,
  code: product?.code,
  name: product?.name,
  description: product?.description,
  category: {
    _id: product?.category?._id,
    code: product?.category?.code,
  },
  reference: product?.reference,
  photoUrl: product?.photoUrl,
  weight: product?.weight,
  volume: product?.volume,
  value: product?.value,
  pickup: product?.pickup,
  refPickupDelivery: product?.refPickupDelivery,
});

export const getStopItemsCount = (items: Items[], isTourRequest: boolean) =>
  isTourRequest ? items?.length || 0 : items.reduce((counter, item) => counter + item?.counter, 0);

export const getRelativeRequestInformation = (status: ERequestStatus, text: any) => {
  switch (status) {
    case ERequestStatus.Requested:
      return `${text.requestQuote.confirmation_request_quote}`;
    case ERequestStatus.Accepted:
      return `${text.requestQuote.accepted_request_quote}`;
    case ERequestStatus.Approved:
      return `${text.requestQuote.approved_request_quote}`;
    default:
      return "";
  }
};

export const removeDuplicateElements = (arrayOfObject: any[], keyToCompare: string) => {
  const arraySetKey = new Set();
  const filteredArr = arrayOfObject.filter((el) => {
    const duplicate = arraySetKey.has(el?.product[keyToCompare]);
    arraySetKey.add(el?.product[keyToCompare]);
    return !duplicate;
  });
  return filteredArr;
};

export const parseTourStops = (templateStops: TemplateStop[]): Stop[] => {
  return templateStops?.reduce((stops, stop) => [...stops, stop], []);
};

export const ProductSizes = ["S", "M", "L", "XL", "XXL"];

export const handleAbilityFields = (row: any, fields: any) => {
  let finalState = true;
  if (typeof row === "object" && typeof fields === "object") {
    const keysToCheck = Object.keys(fields);
    finalState = keysToCheck
      .map((keyToCheck) => {
        if (Array.isArray(fields[keyToCheck])) {
          return fields[keyToCheck].includes(row?.[keyToCheck]);
        }
        if (row?.[keyToCheck] === fields[keyToCheck]) {
          return true;
        }
        return false;
      })
      .every((bool) => bool);
  }
  return finalState;
};
